"use client";

import React, { useEffect, useState, memo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";

/* ---------------------------------- */
/* Styles */
/* ---------------------------------- */

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    width: 480,
    minHeight: 400,
  },
});

const StyledTabs = styled(Tabs)({
  minHeight: 32,
  "& .MuiTab-root": {
    minHeight: 32,
    fontSize: 12,
    padding: "8px 16px",
  },
});

const CompactTextField = styled(TextField)({
  "& .MuiInputBase-root": {
    height: 28,
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 11,
  },
});

const CompactSelect = styled(Select)({
  "& .MuiSelect-select": {
    padding: "4px 8px",
    fontSize: 12,
  },
});

const Section = styled(Box)(({ theme }) => ({
  border: "1px solid #e0e0e0",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const Label = styled(Typography)({
  fontSize: 11,
  marginBottom: 4,
});

/* ---------------------------------- */
/* Constants */
/* ---------------------------------- */

const FONT_OPTIONS = ["Arial", "Tahoma", "Roboto"];
const HEADER_ALIGNMENTS = ["Left", "Center", "Right"];
const CELL_ALIGNMENTS = ["Near", "Center", "Far"];

/* ---------------------------------- */
/* Utils */
/* ---------------------------------- */

const hexToRgb = (hex) => {
  if (!hex?.startsWith("#")) return "";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
};

const rgbToHex = (rgb) => {
  const nums = rgb.split(",").map((n) => parseInt(n.trim(), 10));
  if (nums.length !== 3 || nums.some((n) => isNaN(n) || n < 0 || n > 255))
    return null;
  return `#${nums.map((n) => n.toString(16).padStart(2, "0")).join("")}`;
};

/* ---------------------------------- */
/* Color Field */
/* ---------------------------------- */

const ColorField = memo(({ value, onChange, id }) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
    <input
      type="color"
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ display: "none" }}
    />

    <label
      htmlFor={id}
      style={{
        width: 24,
        height: 24,
        minWidth: 24,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #999",
        borderRadius: 4,
        backgroundColor: value || "#ffffff",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: 12,
          height: 12,
          background: "#fff",
          borderRadius: 2,
          opacity: 0.4,
        }}
      />
    </label>

    <CompactTextField
      value={hexToRgb(value)}
      onChange={(e) => {
        const hex = rgbToHex(e.target.value);
        if (hex) onChange(hex);
      }}
      placeholder="R, G, B"
      size="small"
      // fullWidth
    />
  </Box>
));

/* ---------------------------------- */
/* Main Dialog */
/* ---------------------------------- */

const ColumnSettingsDialog = ({ open, onClose, onOk, selectedColumn }) => {
  const [form, setForm] = useState({
    header: {
      fontFamily: "Arial",
      color: "#8db3e2",
      textAlign: "Center",
    },
    cell: {
      fontFamily: "Tahoma",
      color: "#000000",
      backgroundColor: "#ffffff",
      alignment: "Near",
    },
  });

  /* Sync on open */
  useEffect(() => {
    if (!open || !selectedColumn) return;

    setForm({
      header: {
        fontFamily: selectedColumn?.headerStyles?.["font-family"] || "Arial",
        color: selectedColumn?.headerStyles?.color || "#8db3e2",
        textAlign: selectedColumn?.headerStyles?.["text-align"] || "Center",
      },
      cell: {
        fontFamily: selectedColumn?.appearance?.fontFamily || "Tahoma",
        color: selectedColumn?.appearance?.color || "#000000",
        backgroundColor:
          selectedColumn?.appearance?.backgroundColor || "#ffffff",
        alignment: selectedColumn?.alignment || "Near",
      },
    });
  }, [open, selectedColumn]);

  const update = (path, value) => {
    setForm((prev) => ({
      ...prev,
      [path[0]]: {
        ...prev[path[0]],
        [path[1]]: value,
      },
    }));
  };

  const handleOk = () => {
    onOk({
      ...selectedColumn,
      alignment: form.cell.alignment,
      headerStyles: {
        "font-family": form.header.fontFamily,
        color: form.header.color,
        "text-align": form.header.textAlign,
      },
      appearance: {
        fontFamily: form.cell.fontFamily,
        color: form.cell.color,
        backgroundColor: form.cell.backgroundColor,
      },
    });
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle sx={{ fontSize: 14, p: 1.5 }}>
        Column Setting Details
      </DialogTitle>

      <DialogContent sx={{ p: 1 }}>
        <StyledTabs value={0}>
          <Tab label={selectedColumn?.caption || ""} />
        </StyledTabs>

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Section>
              <SectionTitle>Header Formatting</SectionTitle>

              <Label>Font</Label>
              <CompactSelect
                fullWidth
                value={form.header.fontFamily}
                onChange={(e) =>
                  update(["header", "fontFamily"], e.target.value)
                }
              >
                {FONT_OPTIONS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </CompactSelect>

              <Label>Color</Label>
              <ColorField
                id="header-color"
                value={form.header.color}
                onChange={(v) => update(["header", "color"], v)}
              />

              <Label>Alignment</Label>
              <CompactSelect
                fullWidth
                value={form.header.textAlign}
                onChange={(e) =>
                  update(["header", "textAlign"], e.target.value)
                }
              >
                {HEADER_ALIGNMENTS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </CompactSelect>
            </Section>
          </Grid>

          <Grid item xs={6}>
            <Section>
              <SectionTitle>Cell Formatting</SectionTitle>

              <Label>Font</Label>
              <CompactSelect
                fullWidth
                value={form.cell.fontFamily}
                onChange={(e) => update(["cell", "fontFamily"], e.target.value)}
              >
                {FONT_OPTIONS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </CompactSelect>

              <Label>Text Color</Label>
              <ColorField
                id="cell-color"
                value={form.cell.color}
                onChange={(v) => update(["cell", "color"], v)}
              />

              <Label>Background</Label>
              <ColorField
                id="cell-bg"
                value={form.cell.backgroundColor}
                onChange={(v) => update(["cell", "backgroundColor"], v)}
              />

              <Label>Alignment</Label>
              <CompactSelect
                fullWidth
                value={form.cell.alignment}
                onChange={(e) => update(["cell", "alignment"], e.target.value)}
              >
                {CELL_ALIGNMENTS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </CompactSelect>
            </Section>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1 }}>
        <Button size="small" onClick={onClose}>
          Close
        </Button>
        <Button size="small" variant="contained" onClick={handleOk}>
          OK
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ColumnSettingsDialog;
